/**
 * 認證 API 服務
 * 使用 RTK Query 封裝認證相關的 API 調用
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@/services/baseApi';
import { ILoginParams, ILoginResponse, IUserProfile } from '@/types/auth';
import { ConstantUtil } from '@/utils/ConstantUtil';
import { logService } from '@/utils/logService';

// 創建 API 服務
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    // 登入
    login: builder.mutation<ILoginResponse, ILoginParams>({
      query: (credentials) => {
        logService.logApiRequest(ConstantUtil.API_USER_LOGIN, 'POST', credentials);
        return {
          url: ConstantUtil.API_USER_LOGIN,
          method: 'POST',
          body: credentials,
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          logService.logApiResponse(ConstantUtil.API_USER_LOGIN, 200, data);
          // 業務邏輯處理（localStorage 操作等）應在組件或 controller 層處理
          // 請在調用 login mutation 的地方使用 AuthService.handleLoginSuccess(data)
        } catch (error) {
          logService.logApiError(ConstantUtil.API_USER_LOGIN, error);
        }
      },
    }),
    
    // 登出
    logout: builder.mutation<void, void>({
      query: () => ({
        url: ConstantUtil.API_USER_LOGOUT,
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          // 業務邏輯處理（localStorage 操作等）應在組件或 controller 層處理
          // 請在調用 logout mutation 的地方使用 AuthService.handleLogoutSuccess()
        } catch (error) {
          logService.logApiError(ConstantUtil.API_USER_LOGOUT, error);
        }
      },
    }),
    
    // 獲取用戶資料
    getUserProfile: builder.query<IUserProfile, void>({
      query: () => ConstantUtil.API_USER_PROFILE,
      providesTags: ['User'],
    }),
    
    // 更新用戶資料
    updateUserProfile: builder.mutation<IUserProfile, Partial<IUserProfile>>({
      query: (updates) => ({
        url: ConstantUtil.API_USER_UPDATE,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// 導出自動生成的 hooks
export const {
  useLoginMutation,
  useLogoutMutation,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
} = authApi;
